import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SaveIcon, UserGroupIcon, TrashIcon, TagIcon } from '@heroicons/react/solid'

import { BUTTON_VARIANT_TYPES, NO_ACCESS_TYPES, PERMISSIONS } from "../../../../utils/constants";

import {
    assignApplicationsToUserGroup,
    assignPermissionsToUserGroup,
    assignUsersToUserGroup,
    editUserGroupName,
    getUserPermissions,
    getAllUserGroups,
    getOrganisationApplications,
    getOrganisationUsers,
    deleteUserGroup,
    editUserGroupTags,
    setParentUserGroup
} from "../../../../utils/api";

import Alert, { ALERT_TYPE } from "../../../Auth/Alert";
import Button from "../../../SharedComponents/Button";
import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import DualList from "../../../SharedComponents/Form/DualList";
import Input from "../../../Auth/Input";
import Tag from "../../../SharedComponents/Tag";
import DualistPlaceholder from "../../../SharedComponents/Loading/DualistPlaceholder";
import PillPlaceholder from "../../../SharedComponents/Loading/PillPlaceholder";
import AccessControl from "../../../SharedComponents/AccessControl";
import useMountStatus from '../../../../hooks/useMountStatus';

function EditUserGroup() {
    const [alert, setAlert] = useState('');
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [userGroup, setUserGroup] = useState();
    const [permissions, setPermissions] = useState([]);
    const [applications, setApplications] = useState([]);
    const [users, setUsers] = useState([]);
    const [editedName, setEditedName] = useState();
    const [current, setCurrent] = useState();
    const [options, setOptions] = useState();
    const [editOption, setEditOption] = useState("users");
    const [handleSave, setHandleSave] = useState();
    const [changesMade, setChangesMade] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [tagInput, setTagInput] = useState("");
    const [userGroupTags, setUserGroupTags] = useState([]);
    const [userGroups, setUserGroups] = useState([]);
    const [parentGroupName, setParentGroupName] = useState();
    const unmounted = useMountStatus();

    const location = useLocation();

    const navigate = useNavigate();

    const handleSuccessfulEdit = async (message, userGroupName, editOption) => {
        setAlert(message);
        setAlertType(ALERT_TYPE.SUCCESS);
        await fetchData(userGroupName ? userGroupName : userGroup.name, editOption);
    }

    const handleDeleteUserGroup = async (userGroupName) => {
        setSubmitLoading(true);
        try {
            await deleteUserGroup(userGroupName);

            navigate("/user-groups", {
                state: {
                    successfulDelete: "You've successfully deleted the " + userGroupName + " User group"
                }
            });

        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setSubmitLoading(false);
    }

    const handleEditName = async (originalName, editedName) => {
        setSubmitLoading(true);
        try {
            await editUserGroupName(originalName, editedName);
            await handleSuccessfulEdit(
                "You've successfully renamed usergroup " + userGroup.name + " to " + editedName,
                editedName,
                "name"
            );
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setSubmitLoading(false);
    }

    const handleSetParentGroup = async () => {
        setSubmitLoading(true);
        try {
            await setParentUserGroup(userGroup.name, parentGroupName);
            if (parentGroupName) {
                setAlert(`You've successfully set ${parentGroupName} as parent group of ${userGroup.name}`);
            } else {
                setAlert(`You've successfully set no parent group to ${userGroup.name}`)
            }
            setAlertType(ALERT_TYPE.SUCCESS);
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setSubmitLoading(false);
    };

    const handleEditTags = async (userGroupName, userGroupTags) => {
        setSubmitLoading(true);
        try {
            await editUserGroupTags(userGroupName, userGroupTags);
            await handleSuccessfulEdit(
                "You've successfully updated usergroup tags"
            );
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setSubmitLoading(false);
    }

    const handleAssignApplications = async (applications, userGroupName) => {
        setSubmitLoading(true);
        try {
            await assignApplicationsToUserGroup(userGroupName, applications.map(application => application.identifier));
            await handleSuccessfulEdit("You've successfully edited applications in " + userGroupName, userGroupName, "applications");
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setSubmitLoading(false);
    }

    const handleAssignPermissions = async (permissions, userGroupName) => {
        setSubmitLoading(true);
        try {
            await assignPermissionsToUserGroup(userGroupName, permissions.map(permission => permission.code));
            await handleSuccessfulEdit("You've successfully assigned permissions in " + userGroupName, userGroupName, "permissions");
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setSubmitLoading(false);
    }

    const handleAssignUsers = async (users, userGroupName) => {
        setSubmitLoading(true);
        try {
            await assignUsersToUserGroup(userGroupName, users.map(user => user.userIdentifier));
            await handleSuccessfulEdit("You've successfully added users to " + userGroupName, userGroupName, "users");
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setSubmitLoading(false);
    }

    const handleEditOption = (userEditOption, options, current) => {

        switch (userEditOption ? userEditOption : editOption) {
            case "name":
                setCurrent(userGroup.name);
                setHandleSave(() => handleEditName);
                break;
            case "parentgroup":
                setCurrent(userGroup.name);
                setHandleSave(() => handleEditName);
                break;
            case "tags":
                setHandleSave(() => handleEditTags);
                break;
            case "users":
                setCurrent((current ? current : userGroup.users));
                setHandleSave(() => handleAssignUsers);
                setOptions((options ? options : users)
                    .filter(optionsItem => !(current ? current : userGroup.users)
                        .find(selectedItem => (selectedItem.userIdentifier === optionsItem.userIdentifier))));
                break;
            case "permissions":
                setCurrent((current ? current : userGroup.permissions));
                setHandleSave(() => handleAssignPermissions)
                setOptions((options ? options : permissions)
                    .filter(optionsItem => !(current ? current : userGroup.permissions)
                        .find(selectedItem => (selectedItem.code === optionsItem.code))));
                break;
            case "applications":
                setCurrent((current ? current : userGroup.applications));
                setHandleSave(() => handleAssignApplications);
                setOptions((options ? options : applications)
                    .filter(optionsItem => !(current ? current : userGroup.applications)
                        .find(selectedItem => (selectedItem.name === optionsItem.name))));
                break;
            default:
                setCurrent((current ? current : userGroup.users));
                setHandleSave(() => handleAssignUsers);
                setOptions((options ? options : users)
                    .filter(optionsItem => !(current ? current : userGroup.users)
                        .find(selectedItem => (selectedItem.userIdentifier === optionsItem.userIdentifier))));
                break;
        }
    }

    const handleEditOptionClick = (editOption) => {
        editOption && setEditOption(editOption);

        handleEditOption(editOption)
    };

    const fetchData = async (userGroupName, editOption) => {
        setLoading(true);

        try {
            const userGroupsResponse = await getAllUserGroups();

            const userGroup = userGroupsResponse
                .filter(userGroup => (userGroup.name === userGroupName))[0];

            const usersResponse = await getOrganisationUsers();
            const permissionsResponse = await getUserPermissions();
            const applicationsResponse = await getOrganisationApplications();

            if (unmounted) return;
            setUsers(usersResponse);
            setPermissions(permissionsResponse);
            setApplications(applicationsResponse);
            setUserGroups(userGroupsResponse.filter(userGroup => (userGroup.name !== userGroupName)));
            setParentGroupName(userGroup.parentGroupName);

            switch (editOption) {
                case "name":
                    handleEditOption(editOption, userGroup.name);
                    break;
                case "parentgroup":
                    handleEditOption(editOption, userGroup.name);
                    break;
                case "tags":
                    handleEditOption(editOption, userGroup.tags);
                    break;
                case "users":
                    handleEditOption(editOption, usersResponse, userGroup.users);
                    break;
                case "permissions":
                    handleEditOption(editOption, permissionsResponse, userGroup.permissions);
                    break;
                case "applications":
                    handleEditOption(editOption, applicationsResponse, userGroup.applications);
                    break;
                default:
                    handleEditOption(editOption, usersResponse, userGroup.users);
            }

            setUserGroup(userGroup);
            setUserGroupTags(userGroup.tags);
        } catch (err) {
            if (unmounted) return;
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setLoading(false);
    };

    const handleOnClickSave = () => {
        if (editOption === "name") {
            handleEditName(userGroup.name, editedName);
        } else if (editOption === "tags") {
            handleEditTags(userGroup.name, userGroupTags);
        } else if (editOption === "delete") {
            handleDeleteUserGroup(userGroup.name);
        } else if (editOption === "parentgroup") {
            handleSetParentGroup();
        } else {
            handleSave(current, userGroup.name);
        }
        setChangesMade(false);
    }

    const handleTagsKeyPress = (key) => {
        if (key === 'Enter' && tagInput.length > 1) {
            setUserGroupTags(tags => [...new Set([...tags, tagInput.toLowerCase()])]);
            setTagInput("");
            setChangesMade(true);
        }
    }

    const deleteTag = (tagToDelete) => {
        setUserGroupTags(tags => tags.filter(tag => tag !== tagToDelete));
        setChangesMade(true);
    }

    useEffect(() => {
        if (!location.state) {
            navigate("/user-groups");
        } else {
            location.state.editOption && setEditOption(location.state.editOption);
            fetchData(location.state.userGroupName, location.state.editOption ? location.state.editOption : "users");
        }

        // Some refactoring is needed here, not urgent but there's useCallback/dependency issues with other functions
        // (can't use some functions before initiation) once we add fetchData to dependencies

        /* eslint-disable */
    }, [location.state, navigate])
    /* eslint-enable */

    return (
        <AccessControl requiredPermission={PERMISSIONS.MANAGE_USERS_IN_USER_GROUP} noAccessType={NO_ACCESS_TYPES.BLANK}>
            <DashboardContainer title={"Edit User Group"} titleIcon={<UserGroupIcon className="block h-5 w-5 mr-2"/>}>
                <Alert
                    text={alert}
                    type={alertType}
                    active={alert}
                    close={() => setAlert("")}
                />
                <div className="grid grid-flow-row xl:grid-cols-8 lg:grid-cols-2 gap-6 min-h-0 overflow-x-scroll">
                    <div className={"flex flex-row xl:flex-col gap-3 col-span-2 z-10 w-max xl:w-auto"}>
                        <AccessControl requiredPermission={PERMISSIONS.MANAGE_USERS_IN_USER_GROUP}
                                       noAccessType={NO_ACCESS_TYPES.BLANK}>
                            <div className={
                                "p-3 text-sm rounded-md  flex items-center justify-between font-medium " +
                                "shadow-sm hover:text-secondary-600 transition transition-all cursor-pointer " +
                                (editOption === "users" ? "text-secondary-600 bg-secondary-50 " : "text-blue-gray-600 bg-white ") +
                                (loading && "animate-pulse pointer-events-none")
                            }
                                 onClick={() => handleEditOptionClick("users")}
                            >
                                <span>Users</span>
                                {loading ?
                                    <PillPlaceholder/>
                                    :
                                    userGroup &&
                                    <div className={
                                        "lg:hidden xl:block text-xs font-medium text-secondary-600 bg-secondary-100 p-1 px-2 " +
                                        "rounded-full hidden"
                                    }>
                                        {userGroup.users.length} user{userGroup.users.length !== 1 && "s"}
                                    </div>
                                }
                            </div>
                        </AccessControl>
                        <AccessControl requiredPermission={PERMISSIONS.MANAGE_USER_GROUP_PERMISSIONS}
                                       noAccessType={NO_ACCESS_TYPES.BLANK}>
                            <div
                                className={
                                    "p-3 text-sm rounded-md flex items-center justify-between font-medium " +
                                    "shadow-sm hover:text-secondary-600 transition transition-all cursor-pointer " +
                                    (editOption === "permissions" ? "text-secondary-600 bg-secondary-50 " : "text-blue-gray-600 bg-white ") +
                                    (loading && "animate-pulse pointer-events-none")
                                }
                                onClick={() => handleEditOptionClick("permissions")}
                            >
                                <span>Permissions</span>
                                {loading ?
                                    <PillPlaceholder/>
                                    :
                                    userGroup &&
                                    <div className={
                                        "lg:hidden xl:block text-xs font-medium text-secondary-600 bg-secondary-100 p-1 px-2 " +
                                        "rounded-full hidden"
                                    }>
                                        {userGroup.permissions.length} permission{userGroup.permissions.length !== 1 && "s"}
                                    </div>
                                }
                            </div>
                        </AccessControl>
                        <AccessControl requiredPermission={PERMISSIONS.MANAGE_USER_GROUP_APPLICATIONS}
                                       noAccessType={NO_ACCESS_TYPES.BLANK}>
                            <div
                                className={
                                    "p-3 text-sm rounded-md flex items-center justify-between font-medium " +
                                    "shadow-sm hover:text-secondary-600 transition transition-all cursor-pointer " +
                                    (editOption === "applications" ? "text-secondary-600 bg-secondary-50 " : "text-blue-gray-600 bg-white ") +
                                    (loading && "animate-pulse pointer-events-none")
                                }
                                onClick={() => handleEditOptionClick("applications")}
                            >
                                <span>Applications</span>
                                {loading ?
                                    <PillPlaceholder/>
                                    :
                                    userGroup &&
                                    <div
                                        className={"hidden xl:block text-xs font-medium text-secondary-600 bg-secondary-100 p-1 px-2 rounded-full"}>
                                        {userGroup.applications.length} application{userGroup.applications.length !== 1 && "s"}
                                    </div>
                                }
                            </div>
                        </AccessControl>
                        <AccessControl requiredPermission={PERMISSIONS.EDIT_USER_GROUPS}
                                       noAccessType={NO_ACCESS_TYPES.BLANK}>
                            <div
                                className={
                                    "p-3 text-sm rounded-md  flex items-center justify-between font-medium " +
                                    "shadow-sm hover:text-secondary-600 transition transition-all cursor-pointer " +
                                    (editOption === "name" ? "text-secondary-600 bg-secondary-50 " : "text-blue-gray-600 bg-white ") +
                                    (loading && "animate-pulse pointer-events-none")
                                }
                                onClick={() => handleEditOptionClick("name")}
                            >
                                <span>Name</span>

                                {loading ?
                                    <PillPlaceholder/>
                                    :
                                    userGroup &&
                                    <div className={
                                        "hidden xl:block text-xs font-medium text-secondary-600 bg-secondary-100 p-1 px-2 " +
                                        "rounded-full"
                                    }>
                                        {userGroup.name}
                                    </div>
                                }

                            </div>
                        </AccessControl>
                        <AccessControl requiredPermission={PERMISSIONS.EDIT_USER_GROUPS}
                                       noAccessType={NO_ACCESS_TYPES.BLANK}>
                            <div
                                className={
                                    "p-3 text-sm rounded-md  flex items-center justify-between font-medium " +
                                    "shadow-sm hover:text-secondary-600 transition transition-all cursor-pointer " +
                                    (editOption === "parentgroup" ? "text-secondary-600 bg-secondary-50 " : "text-blue-gray-600 bg-white ") +
                                    (loading && "animate-pulse pointer-events-none")
                                }
                                onClick={() => handleEditOptionClick("parentgroup")}
                            >
                                <span>Parent Group</span>

                                {loading ?
                                    <PillPlaceholder/>
                                    :
                                    userGroup &&
                                    <div className={
                                        "hidden xl:block text-xs font-medium text-secondary-600 bg-secondary-100 p-1 px-2 " +
                                        "rounded-full"
                                    }>
                                        {parentGroupName || "No parent group"}
                                    </div>
                                }

                            </div>
                        </AccessControl>
                        <AccessControl requiredPermission={PERMISSIONS.EDIT_USER_GROUP_TAGS}
                                       noAccessType={NO_ACCESS_TYPES.BLANK}>
                            <div
                                className={
                                    "p-3 text-sm rounded-md  flex items-center justify-between font-medium " +
                                    "shadow-sm hover:text-secondary-600 transition transition-all cursor-pointer " +
                                    (editOption === "tags" ? "text-secondary-600 bg-secondary-50 " : "text-blue-gray-600 bg-white ") +
                                    (loading && "animate-pulse pointer-events-none")
                                }
                                onClick={() => handleEditOptionClick("tags")}
                            >
                                <span>Tags</span>

                                {loading ?
                                    <PillPlaceholder/>
                                    :
                                    userGroup &&
                                    <div className={
                                        "hidden xl:block text-xs font-medium text-secondary-600 bg-secondary-100 p-1 px-2 " +
                                        "rounded-full"
                                    }>
                                        {userGroup.tags.length} tag{userGroup.tags.length !== 1 && "s"}
                                    </div>
                                }
                            </div>
                        </AccessControl>
                        <AccessControl requiredPermission={PERMISSIONS.DELETE_USER_GROUPS}
                                       noAccessType={NO_ACCESS_TYPES.BLANK}>
                            <div
                                className={
                                    "p-3 text-sm rounded-md  flex items-center justify-between font-medium " +
                                    "shadow-sm hover:text-red-600 transition transition-all cursor-pointer " +
                                    (editOption === "delete" ? "text-red-600 bg-red-50 " : "text-red-500 bg-white ") +
                                    (loading && "animate-pulse pointer-events-none")
                                }
                                onClick={() => handleEditOptionClick("delete")}
                            >
                                <span>Delete</span>
                                <TrashIcon className="block h-4 w-4 ml-2"/>
                            </div>
                        </AccessControl>
                    </div>
                    <div className={"col-span-6"}>
                        <div className={
                            "border-0 rounded-lg relative flex flex-col w-full bg-white outline-none " +
                            "focus:outline-none shadow-sm"
                        }>
                            <div className="flex items-start justify-between pt-4 pb-2 px-4 rounded-t">
                                <h3 className="text-md font-medium leading-6 text-indigo-900 capitalize">
                                    {editOption === "delete" ?
                                        <span className="flex">Delete <div
                                            className="ml-1 text-secondary-600">{(userGroup && userGroup.name)}</div></span> :
                                        editOption === "users" ?
                                            <span className="flex">Edit {editOption} in <div
                                                className="ml-1 text-secondary-600">{(userGroup && userGroup.name)}</div></span> :
                                            editOption === "users" ?
                                                <span className="flex">Edit {editOption} of <div
                                                    className="ml-1 text-secondary-600">{(userGroup && userGroup.name)}</div></span> :
                                                editOption === "parentgroup" ?
                                                    <span className="flex">Edit parent group for <div
                                                        className="ml-1 text-secondary-600">{(userGroup && userGroup.name)}</div></span> :
                                                    <span className="flex">Edit {editOption} for <div
                                                        className="ml-1 text-secondary-600">{(userGroup && userGroup.name)}</div></span>
                                    }
                                </h3>
                            </div>
                            {editOption === "name" ?
                                <div className="px-4">
                                    <p className="text-sm text-gray-600">
                                        Use the input box below to edit the user group name.
                                    </p>
                                </div> :
                                editOption === "parentgroup" ?
                                    <div className="px-4">
                                        <p className="text-sm text-gray-600">
                                            Use the drop down list below to select a parent user group.
                                        </p>
                                    </div>
                                    : editOption === "tags" ?
                                        <div className="px-4">
                                            <p className="text-sm text-gray-600">
                                                Use the input box below to edit the user group tags.
                                            </p>
                                        </div>
                                        : editOption === "delete" ?
                                            <div className="px-4">
                                                <p className="text-sm text-gray-600">
                                                    Click the button below to delete User Group
                                                </p>
                                            </div>
                                            :
                                            <div className="px-4">
                                                <p className="text-sm text-gray-600">
                                                    Use the dual list below
                                                    to {editOption === "name" ? "edit the" : "add or remove"} {editOption} from
                                                    your
                                                    user group.
                                                </p>
                                            </div>
                            }
                            <div className="relative px-4 flex-auto mt-4 mb-4">

                                {editOption === "name" ?
                                    <Input
                                        type="text"
                                        name="User Group name"
                                        id="userGroupName"
                                        placeholder={userGroup && userGroup.name}
                                        onChange={(textInput) => {
                                            setEditedName(textInput);
                                            setChangesMade(true);
                                        }}
                                        Icon={<UserGroupIcon className="h-4 w-4 text-gray-500"/>}
                                    />
                                    :
                                    editOption === "parentgroup" ?
                                        <Input
                                            type="select"
                                            name="Parent Group Name"
                                            id="userGroupName"
                                            placeholder={userGroup && userGroup.name}
                                            onChange={(value) => {
                                                setParentGroupName(value.length > 0 ? value : null);
                                                setChangesMade(true);
                                            }}
                                            Icon={<UserGroupIcon className="h-4 w-4 text-gray-500"/>}
                                            selectOptions={[{
                                                value: "",
                                                text: "No parent group",
                                                selected: parentGroupName === null
                                            }, ...userGroups.map(group => ({
                                                value: group.name,
                                                text: group.name,
                                                selected: group.name === parentGroupName
                                            }))]}
                                        /> :
                                        editOption === "tags" ?
                                            <>
                                                <Input
                                                    id="tags"
                                                    type="text"
                                                    name="Tags"
                                                    placeholder="Enter tag and press enter key"
                                                    value={tagInput}
                                                    onChange={setTagInput}
                                                    Icon={<TagIcon className="h-4 w-4 text-gray-500"/>}
                                                    onKeyPress={handleTagsKeyPress}
                                                />
                                                <div className="flex flex-wrap gap-2">
                                                    {userGroupTags.map(tag => <Tag key={tag} text={tag}
                                                                                   extraStyling="mr-2 mt-4"
                                                                                   onDelete={() => deleteTag(tag)}/>)}
                                                </div>
                                            </>
                                            :
                                            editOption === "delete" ?
                                                <>
                                                    <div className={
                                                        "text-sm w-full mx-auto rounded-md px-3 py-3 info bg-red-100 " +
                                                        "text-red-700 align-center justify-center"
                                                    }>
                                                        Warning: <span className={"font-medium underline"}> Removing a group is not reversible</span>.
                                                        Users within the group will be removed and will no longer retain
                                                        the permissions they previously had.
                                                    </div>
                                                </>
                                                :
                                                loading ?
                                                    <DualistPlaceholder/>
                                                    :
                                                    options && current &&
                                                    <DualList
                                                        options={options}
                                                        setOptions={setOptions}
                                                        current={current}
                                                        setCurrent={setCurrent}
                                                        itemName={editOption}
                                                        handleSave={handleSave}
                                                        setChangesMade={setChangesMade}
                                                    />
                                }

                            </div>
                            <div className={
                                "p-4 flex items-center justify-end text-right rounded-md " +
                                (editOption === "delete" ?
                                    "bg-gradient-to-r from-red-50 to-red-100" :
                                    "bg-gradient-to-l from-primary-50 to-secondary-50")
                            }>
                                <div className="flex flex-row gap-3">
                                    {editOption === "delete" ?
                                        <Button
                                            colour={BUTTON_VARIANT_TYPES.RED}
                                            text="Delete User Group"
                                            icon={<TrashIcon className="block h-4 w-4 mr-2"/>}
                                            iconPositionLeft
                                            onClick={handleOnClickSave}
                                            loading={submitLoading}
                                        />
                                        :
                                        <Button
                                            text="Save changes"
                                            icon={<SaveIcon className="block h-4 w-4 mr-2"/>}
                                            iconPositionLeft
                                            onClick={handleOnClickSave}
                                            loading={submitLoading}
                                            disabled={!changesMade}
                                            extraStyling={
                                                "text-white focus:ring-inset focus:ring-1 bg-secondary-500 " +
                                                "hover:bg-secondary-600 focus:outline-none focus:ring-secondary-500"
                                            }
                                        />
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </DashboardContainer>
        </AccessControl>
    )
}

export default EditUserGroup;
